import { useDeferredValue, useEffect, useMemo, useRef, useState } from 'react';
import {
  MapContainer,
  TileLayer,
  Marker,
  Popup,
  CircleMarker,
  AttributionControl,
  useMap,
} from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { useStoreMap } from '../../api/hooks/useStoreMap';
import { useStores } from '../../api/hooks/useStores';
import './StoreLocator.css';

const CLOSE_MAP_ZOOM = 18;

// CARTO "Voyager" basemap — a clean, light-tinted style built on
// OpenStreetMap data. Free, no API key / billing required, and much crisper
// than the raw OSM tiles. Attribution to OSM + CARTO is required by license.
const TILE_URL =
  'https://{s}.basemaps.cartocdn.com/rastertiles/voyager/{z}/{x}/{y}.png';
const TILE_SUBDOMAINS = 'abcd';
const TILE_ATTRIBUTION =
  '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> &copy; <a href="https://carto.com/attributions">CARTO</a>';

const formatDistance = (distanceKm) => {
  if (!Number.isFinite(distanceKm)) {
    return null;
  }

  if (distanceKm < 1) {
    return `${Math.round(distanceKm * 1000)} m away`;
  }

  return `${distanceKm.toFixed(1)} km away`;
};

// Build a Leaflet marker icon from an inline SVG (keeps the same pin look the
// Google map used, but rendered as a plain data-URL icon so we avoid Leaflet's
// broken default image-path handling under the bundler).
const buildStoreMarkerIcon = (isSelected = false) => {
  // Compact pin. Leaflet markers are fixed screen-pixel size (they do not grow
  // with zoom, by design), so the goal here is a size that reads clearly at any
  // zoom without dominating the map — smaller on phones, a touch larger on wide
  // screens, and slightly bigger when the pin is the selected one.
  const isNarrow = typeof window !== 'undefined' && window.innerWidth < 640;
  const base = isNarrow ? 22 : 26;

  const width = isSelected ? base + 4 : base;
  const height = Math.round(width * 1.32);
  const circleRadius = Math.round(width * 0.38);
  const centerX = width / 2;
  const centerY = circleRadius + 2;
  const tailSpread = Math.round(width * 0.24);
  const innerR = Math.max(3, Math.round(circleRadius * 0.42));
  const fillColor = isSelected ? '#009fb7' : '#ef4444';
  const shadowOpacity = isSelected ? '0.3' : '0.22';

  const svg = `
    <svg xmlns="http://www.w3.org/2000/svg" width="${width}" height="${height}" viewBox="0 0 ${width} ${height}">
      <filter id="shadow" x="-40%" y="-20%" width="180%" height="160%">
        <feDropShadow dx="0" dy="2.5" stdDeviation="2" flood-color="#0f172a" flood-opacity="${shadowOpacity}"/>
      </filter>
      <g filter="url(#shadow)">
        <path d="M${centerX - tailSpread} ${centerY + circleRadius - 2} L${centerX} ${height - 2} L${centerX + tailSpread} ${centerY + circleRadius - 2} Z" fill="${fillColor}" stroke="#ffffff" stroke-width="2.2" stroke-linejoin="round"/>
        <circle cx="${centerX}" cy="${centerY}" r="${circleRadius}" fill="${fillColor}" stroke="#ffffff" stroke-width="2.2"/>
        <circle cx="${centerX}" cy="${centerY}" r="${innerR}" fill="#ffffff"/>
      </g>
    </svg>
  `;

  return L.icon({
    iconUrl: `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(svg)}`,
    iconSize: [width, height],
    iconAnchor: [centerX, height - 1],
    popupAnchor: [0, -(height - 4)],
  });
};

// Keeps the Leaflet map in sync with the mapCenter / mapZoom state that the
// store-map hook drives (store selection, "use my location", initial store).
// Manual dragging/zooming by the user never writes back to that state, so it
// stays put and does not fight the user.
function MapController({ center, zoom, mapRef }) {
  const map = useMap();

  useEffect(() => {
    mapRef.current = map;
    // Leaflet renders gray tiles if the container gained its size after the
    // map initialized (flex panel). Recompute once mounted.
    const timer = setTimeout(() => map.invalidateSize(), 0);
    return () => clearTimeout(timer);
  }, [map, mapRef]);

  useEffect(() => {
    if (center && Number.isFinite(center.lat) && Number.isFinite(center.lng)) {
      map.setView([center.lat, center.lng], zoom ?? map.getZoom());
    }
  }, [center, zoom, map]);

  return null;
}

// One-time framing: when the map first opens without the user's location,
// zoom out to fit every store pin so people get an overview instead of
// landing tight on a single store.
function FitAllOnce({ points, enabled }) {
  const map = useMap();
  const doneRef = useRef(false);

  useEffect(() => {
    if (doneRef.current || !enabled || points.length < 2) {
      return;
    }
    doneRef.current = true;
    const bounds = L.latLngBounds(points);
    map.fitBounds(bounds, { padding: [48, 48], maxZoom: 14 });
  }, [points, enabled, map]);

  return null;
}

export default function StoreLocator() {
  const { stores, loading, error } = useStores();
  const {
    userLocation,
    locationLoading,
    locationError,
    findUserLocation,
    mapCenter,
    mapZoom,
    selectedStore,
    setSelectedStore,
    searchRadius,
    storesWithDistance,
    nearbyStores,
    setMapCenter,
    setMapZoom,
    handleStoreMarkerClick,
  } = useStoreMap(stores);

  const [searchQuery, setSearchQuery] = useState('');
  const mapRef = useRef(null);
  const activeCardRef = useRef(null);
  const deferredSearchQuery = useDeferredValue(searchQuery);

  useEffect(() => {
    findUserLocation();
  }, [findUserLocation]);

  const searchableStores = useMemo(() => {
    if (userLocation) {
      const sorted = [...storesWithDistance].sort((a, b) => {
        if (!Number.isFinite(a.distance_km) && !Number.isFinite(b.distance_km)) return 0;
        if (!Number.isFinite(a.distance_km)) return 1;
        if (!Number.isFinite(b.distance_km)) return -1;
        return a.distance_km - b.distance_km;
      });
      const storesWithoutCoordinates = stores.filter((store) => !store.hasCoordinates);

      return [...sorted, ...storesWithoutCoordinates];
    }

    return stores;
  }, [stores, storesWithDistance, userLocation]);

  const filteredStores = useMemo(() => {
    const query = deferredSearchQuery.trim().toLowerCase();
    if (!query) {
      return searchableStores;
    }

    return searchableStores.filter((store) =>
      [store.name, store.city, store.address].some((value) =>
        String(value || '').toLowerCase().includes(query)
      )
    );
  }, [deferredSearchQuery, searchableStores]);

  const mapStores = useMemo(
    () =>
      filteredStores.filter(
        (store) => Number.isFinite(store.lat) && Number.isFinite(store.lng)
      ),
    [filteredStores]
  );

  const filteredStoreNumbers = useMemo(() => {
    const storeNumbers = new Map();
    filteredStores.forEach((store, index) => {
      storeNumbers.set(store.id, index + 1);
    });

    return storeNumbers;
  }, [filteredStores]);

  // Ensure coordinates are safe numeric values before passing to the map
  const safeMapStores = useMemo(() => {
    const toNum = (v) => {
      if (v === null || v === undefined || v === '') return NaN;
      if (typeof v === 'number') return Number.isFinite(v) ? v : NaN;
      const parsed = parseFloat(String(v).trim());
      return Number.isFinite(parsed) ? parsed : NaN;
    };

    return mapStores
      .map((s) => ({ ...s, lat: toNum(s.lat), lng: toNum(s.lng) }))
      .filter((s) => Number.isFinite(s.lat) && Number.isFinite(s.lng));
  }, [mapStores]);

  const selectedStoreDetails = useMemo(() => {
    if (!selectedStore) {
      return null;
    }

    return (
      filteredStores.find((store) => store.id === selectedStore.id) ||
      searchableStores.find((store) => store.id === selectedStore.id) ||
      selectedStore
    );
  }, [filteredStores, searchableStores, selectedStore]);

  useEffect(() => {
    if (!selectedStore && filteredStores.length > 0) {
      setSelectedStore(filteredStores[0]);
      return;
    }

    if (
      selectedStore &&
      filteredStores.length > 0 &&
      !filteredStores.some((store) => store.id === selectedStore.id)
    ) {
      setSelectedStore(filteredStores[0]);
    }
  }, [filteredStores, selectedStore, setSelectedStore]);

  // Keep the highlighted store card visible in the list when the selection
  // changes from the map (clicking a marker). `block: 'nearest'` means an
  // already-visible card won't cause any jump.
  useEffect(() => {
    activeCardRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
  }, [selectedStoreDetails?.id]);

  const getDirections = (store) => {
    if (!store) return;

    if (Number.isFinite(store.lat) && Number.isFinite(store.lng)) {
      const url = `https://www.google.com/maps/dir/?api=1&destination=${store.lat},${store.lng}`;
      window.open(url, '_blank', 'noopener,noreferrer');
      return;
    }

    const address = `${store.name}, ${store.address}, ${store.city}`;
    const url = `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(address)}`;
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  const openInMaps = (store) => {
    if (!store) return;

    if (Number.isFinite(store.lat) && Number.isFinite(store.lng)) {
      const url = `https://www.google.com/maps/search/?api=1&query=${store.lat},${store.lng}`;
      window.open(url, '_blank', 'noopener,noreferrer');
      return;
    }

    const address = `${store.name}, ${store.address}, ${store.city}`;
    const url = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(address)}`;
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  const selectStoreFromList = (store) => {
    handleStoreMarkerClick(store);
  };

  const handleMapMarkerClick = (store) => {
    if (!Number.isFinite(store?.lat) || !Number.isFinite(store?.lng)) {
      return;
    }

    const currentZoom = mapRef.current?.getZoom();
    const nextZoom =
      !Number.isFinite(currentZoom) || currentZoom < CLOSE_MAP_ZOOM
        ? CLOSE_MAP_ZOOM
        : currentZoom;

    setSelectedStore(store);
    setMapCenter({ lat: store.lat, lng: store.lng });
    setMapZoom(nextZoom);
  };

  const handleStoreCardKeyDown = (event, store) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      selectStoreFromList(store);
    }
  };

  const hasInitialCenter =
    mapCenter && Number.isFinite(mapCenter.lat) && Number.isFinite(mapCenter.lng);

  const locationMessage = locationLoading
    ? 'Finding your location...'
    : locationError
      ? locationError
      : userLocation
        ? `${nearbyStores.length} store${nearbyStores.length === 1 ? '' : 's'} within ${searchRadius} km of your location`
        : 'Enable location to see nearest stores first';

  return (
    <div className="store-locator-page">
      <div className="store-locator-container">
        <header className="store-locator-header">
          <h1 className="store-locator-title">
            Find a <span className="highlight">Store</span>
          </h1>
          <p className="store-locator-subtitle">
            Discover Chilltree products at verified retail partners near you.
          </p>

          <div className="store-toolbar">
            <div className="store-locator-search-wrap">
              <div className="search-input-inner">
                <span className="search-icon" aria-hidden="true"></span>
                <input
                  type="text"
                  className="store-locator-search"
                  placeholder="Search by city, name, or address"
                  aria-label="Search stores by city, name, or address"
                  value={searchQuery}
                  onChange={(event) => setSearchQuery(event.target.value)}
                />
              </div>
            </div>
          </div>

          <div className="location-status-row">
            <span className="results-count">
              {filteredStores.length} {filteredStores.length === 1 ? 'store' : 'stores'}
            </span>
            <span className="location-status-text">{locationMessage}</span>
          </div>
        </header>

        <div className="store-locator-grid">
          <aside className="store-locator-list-panel">
            <div className="list-header">
              <div>
                <span className="list-heading">Store List</span>
                <span className="list-subtitle">{safeMapStores.length} marked on map</span>
              </div>
              <span className="list-count-pill">{filteredStores.length}</span>
            </div>

            <div className="store-list-scroll">
              {loading ? (
                <div className="store-locator-loading">
                  <div className="skeleton-card"></div>
                  <div className="skeleton-card"></div>
                  <div className="skeleton-card"></div>
                </div>
              ) : error ? (
                <div className="store-locator-empty">
                  <p>We could not load store locations right now.</p>
                </div>
              ) : filteredStores.length > 0 ? (
                filteredStores.map((store) => {
                  const storeNumber = filteredStoreNumbers.get(store.id);
                  const isActive = selectedStoreDetails?.id === store.id;

                  return (
                    <div
                      key={store.id}
                      ref={isActive ? activeCardRef : null}
                      className={`store-card-v2 ${isActive ? 'active' : ''}`}
                      onClick={() => selectStoreFromList(store)}
                      onKeyDown={(event) => handleStoreCardKeyDown(event, store)}
                      role="button"
                      tabIndex={0}
                      aria-current={isActive ? 'true' : undefined}
                    >
                      <span className="store-list-marker" aria-hidden="true">
                        {storeNumber}
                      </span>
                      <div className="store-card-content">
                        <div className="store-card-header">
                          <h3 className="store-name-v2">{store.name}</h3>
                          <span className="verified-tag">Verified</span>
                        </div>
                        <p className="store-addr-v2">{store.address}</p>
                        <p className="store-meta-v2">
                          {[store.city, store.mobile].filter(Boolean).join(' | ')}
                        </p>
                        <div className="store-distance-row">
                          <span className="distance-pill">
                            {formatDistance(store.distance_km) || 'Distance unavailable'}
                          </span>
                          {!store.hasCoordinates && (
                            <span className="coordinates-warning">Map pin unavailable</span>
                          )}
                        </div>

                      </div>
                    </div>
                  );
                })
              ) : (
                <div className="store-locator-empty">
                  <p>No stores found matching your search.</p>
                  <button
                    type="button"
                    className="reset-search-btn"
                    onClick={() => setSearchQuery('')}
                  >
                    Clear Search
                  </button>
                </div>
              )}
            </div>
          </aside>

          <main className="store-locator-detail-panel">
            {selectedStoreDetails ? (
              <div className="store-detail-view">
                <div className="store-map-wrap">
                  {safeMapStores.length > 0 && (
                    <button
                      type="button"
                      className={`map-location-control ${locationLoading ? 'is-loading' : ''}`}
                      onClick={findUserLocation}
                      disabled={locationLoading}
                      aria-label={locationLoading ? 'Finding your location' : 'Use my location'}
                      title={locationLoading ? 'Finding your location' : 'Use my location'}
                    >
                      <span className="map-location-icon" aria-hidden="true"></span>
                    </button>
                  )}

                  {mapStores.length === 0 ? (
                    <div className="map-state">
                      <p>No map-ready coordinates are available for these stores yet.</p>
                    </div>
                  ) : safeMapStores.length === 0 || !hasInitialCenter ? (
                    <div className="map-state">
                      <p>Map data is loading or store coordinates are invalid.</p>
                    </div>
                  ) : (
                    <MapContainer
                      center={[mapCenter.lat, mapCenter.lng]}
                      zoom={mapZoom}
                      scrollWheelZoom
                      attributionControl={false}
                      style={{ width: '100%', height: '100%', minHeight: '390px' }}
                    >
                      <MapController
                        center={mapCenter}
                        zoom={mapZoom}
                        mapRef={mapRef}
                      />
                      <FitAllOnce
                        points={safeMapStores.map((s) => [s.lat, s.lng])}
                        enabled={!userLocation}
                      />
                      <AttributionControl position="bottomright" prefix={false} />
                      <TileLayer
                        url={TILE_URL}
                        subdomains={TILE_SUBDOMAINS}
                        attribution={TILE_ATTRIBUTION}
                        maxZoom={20}
                      />

                      {userLocation &&
                        Number.isFinite(Number(userLocation.lat)) &&
                        Number.isFinite(Number(userLocation.lng)) && (
                          <CircleMarker
                            center={[Number(userLocation.lat), Number(userLocation.lng)]}
                            radius={8}
                            pathOptions={{
                              color: '#ffffff',
                              weight: 2,
                              fillColor: '#0f766e',
                              fillOpacity: 1,
                            }}
                          >
                            <Popup>Your location</Popup>
                          </CircleMarker>
                        )}

                      {safeMapStores.map((store) => {
                        const storeNumber = filteredStoreNumbers.get(store.id);
                        const isSelectedMarker = selectedStoreDetails?.id === store.id;

                        return (
                          <Marker
                            key={`${store.id}-${store.lat}-${store.lng}`}
                            position={[store.lat, store.lng]}
                            title={store.name}
                            icon={buildStoreMarkerIcon(isSelectedMarker)}
                            zIndexOffset={isSelectedMarker ? 1000 : storeNumber || 1}
                            eventHandlers={{
                              click: () => handleMapMarkerClick(store),
                            }}
                          >
                            <Popup>
                              <div className="map-info-window">
                                <strong>{store.name}</strong>
                                <p>
                                  {[
                                    store.address,
                                    store.city,
                                    store.state,
                                    store.pincode,
                                  ]
                                    .filter(Boolean)
                                    .join(', ')}
                                </p>
                                <button
                                  type="button"
                                  className="map-info-link"
                                  onClick={() => openInMaps(store)}
                                >
                                  View on Google Maps
                                </button>
                              </div>
                            </Popup>
                          </Marker>
                        );
                      })}
                    </MapContainer>
                  )}
                </div>

                <div className="store-detail-info">
                  <div className="detail-header">
                    <h2 className="detail-title">{selectedStoreDetails.name}</h2>
                    <span className="detail-badge">Verified Partner</span>
                  </div>

                  <div className="detail-grid">
                    <div className="detail-item">
                      <span className="detail-icon">Location</span>
                      <div className="detail-text">
                        <label>Address</label>
                        <p>
                          {[
                            selectedStoreDetails.address,
                            selectedStoreDetails.city,
                            selectedStoreDetails.state,
                            selectedStoreDetails.pincode,
                          ]
                            .filter(Boolean)
                            .join(', ')}
                        </p>
                      </div>
                    </div>

                    <div className="detail-item">
                      <span className="detail-icon">Call</span>
                      <div className="detail-text">
                        <label>Phone</label>
                        <p>{selectedStoreDetails.mobile || 'Not provided'}</p>
                      </div>
                    </div>

                    <div className="detail-item">
                      <span className="detail-icon">Distance</span>
                      <div className="detail-text">
                        <label>Nearest Match</label>
                        <p>{formatDistance(selectedStoreDetails.distance_km) || 'Location needed'}</p>
                      </div>
                    </div>
                  </div>

                  <div className="detail-actions-large">
                    <button
                      type="button"
                      className="btn-primary-v2"
                      onClick={() => getDirections(selectedStoreDetails)}
                    >
                      Get Driving Directions
                    </button>
                    <button
                      type="button"
                      className="btn-secondary-v2"
                      onClick={() => openInMaps(selectedStoreDetails)}
                    >
                      View on Google Maps
                    </button>
                  </div>
                </div>
              </div>
            ) : (
              <div className="no-selection-placeholder">
                <p>Select a store from the list to view location details.</p>
              </div>
            )}
          </main>
        </div>

        <section className="store-features">
          <div className="feature-item-v2">
            <span className="feature-icon-v2">1</span>
            <div className="feature-content-v2">
              <h3>Verified Partners</h3>
              <p>Only active and approved Chilltree retail locations are shown here.</p>
            </div>
          </div>
          <div className="feature-item-v2">
            <span className="feature-icon-v2">2</span>
            <div className="feature-content-v2">
              <h3>Nearest First</h3>
              <p>Your browser location is used to surface the closest stores first.</p>
            </div>
          </div>
          <div className="feature-item-v2">
            <span className="feature-icon-v2">3</span>
            <div className="feature-content-v2">
              <h3>Quick Directions</h3>
              <p>Open turn-by-turn directions or the store pin directly in Google Maps.</p>
            </div>
          </div>
        </section>
      </div>
    </div>
  );
}
